// What a person attached to a message when they sent it (files, sources, results, a selected passage, the part of an App they
// pointed at with Select). Captured once at submit, kept for the live session, and written with the user message as ONE data part
// (`data-ponevaContext`) so the same chips are on the message after a reload. Model replay ignores data parts; nothing here is
// authority, a grant or a model instruction — it is a record of what the composer showed.
import { useSyncExternalStore } from 'react';

export type MessageContextChip =
  // `conversations`: shopper conversations asked about from the Storefront page.
  | { kind: 'file' | 'source' | 'result' | 'conversations'; label: string }
  | { kind: 'passage' }
  // `of: 'assistant'`: a part of a store's shopping assistant (Select in the storefront canvas).
  | { kind: 'selection'; text?: string; role?: string; tag?: string; of?: 'assistant' };

export const MESSAGE_CONTEXT_PART = 'data-ponevaContext';
export type MessageContextPart = {
  type: typeof MESSAGE_CONTEXT_PART;
  data: { chips: MessageContextChip[] };
};

const MAX_CHIPS = 24;
const MAX_LABEL = 200;
const clip = (value: unknown, max = MAX_LABEL) =>
  typeof value === 'string' ? value.trim().slice(0, max) : '';

/** Only well-formed, bounded chips survive: this is read from stored history and from the live store alike. */
export function parseChips(value: unknown): MessageContextChip[] {
  if (!Array.isArray(value)) return [];
  const chips: MessageContextChip[] = [];
  for (const item of value.slice(0, MAX_CHIPS)) {
    const raw = item as Record<string, unknown> | null;
    if (!raw || typeof raw !== 'object') continue;
    if (
      raw.kind === 'file' ||
      raw.kind === 'source' ||
      raw.kind === 'result' ||
      raw.kind === 'conversations'
    ) {
      const label = clip(raw.label);
      if (label) chips.push({ kind: raw.kind, label });
    } else if (raw.kind === 'passage') chips.push({ kind: 'passage' });
    else if (raw.kind === 'selection')
      chips.push({
        kind: 'selection',
        ...(clip(raw.text, 300) ? { text: clip(raw.text, 300) } : {}),
        ...(clip(raw.role, 40) ? { role: clip(raw.role, 40) } : {}),
        ...(clip(raw.tag, 40) ? { tag: clip(raw.tag, 40) } : {}),
        ...(raw.of === 'assistant' ? { of: 'assistant' as const } : {}),
      });
  }
  return chips;
}

/** The chip data stored on a message's own parts (a reloaded or shared chat), or null. */
export function chipsFromParts(
  parts: readonly unknown[] | undefined | null,
): MessageContextChip[] | null {
  for (const part of parts ?? []) {
    const value = part as { type?: unknown; data?: { chips?: unknown } } | null;
    if (value?.type === MESSAGE_CONTEXT_PART) {
      const chips = parseChips(value.data?.chips);
      return chips.length ? chips : null;
    }
  }
  return null;
}

const live = new Map<string, MessageContextChip[]>();
const listeners = new Set<() => void>();
const key = (threadId: string, messageId: string) => `${threadId}\0${messageId}`;
const emit = () => listeners.forEach((listener) => listener());

export function rememberMessageContext(
  threadId: string,
  messageId: string,
  chips: MessageContextChip[],
) {
  const clean = parseChips(chips);
  if (!clean.length) return;
  live.set(key(threadId, messageId), clean);
  emit();
}
export const recalledMessageContext = (threadId: string, messageId: string) =>
  live.get(key(threadId, messageId)) ?? null;
export function useMessageContext(threadId: string | null, messageId: string) {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
    () => (threadId ? recalledMessageContext(threadId, messageId) : null),
    () => null,
  );
}
export const resetMessageContextForTests = () => {
  live.clear();
  emit();
};

/** The message as it is written to history: the remembered chips become one data part, once. */
export function withMessageContext<T extends { id: string; role: string; parts: unknown[] }>(
  threadId: string,
  message: T,
): T {
  if (message.role !== 'user') return message;
  if (chipsFromParts(message.parts)) return message;
  const chips = recalledMessageContext(threadId, message.id);
  if (!chips) return message;
  const part: MessageContextPart = { type: MESSAGE_CONTEXT_PART, data: { chips } };
  return { ...message, parts: [...message.parts, part] };
}
